import React, { useEffect, useMemo, useState } from "react";
import BusinessLayoutV2 from "../../components/BusinessLayoutV2";
import {
    getBusinessById,
    getEmployeeByBusiness,
    getCompletedPayoutsByBusinessID,
} from "../../services/firestoreService";
import { useAuth } from "../../context/AuthProvider";

const getTimestampDate = (value) => {
    if (!value) return null;
    if (value?.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTime = (value) => {
    const date = getTimestampDate(value);
    return date ? date.toLocaleString() : "-";
};

export default function BusinessPayoutsV2() {
    const { userData } = useAuth();

    const [business, setBusiness] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [completedPayouts, setCompletedPayouts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            if (!userData?.businessID) return;
            setIsLoading(true);

            const [businessData, employeeData, completed] = await Promise.all([
                getBusinessById(userData.businessID),
                getEmployeeByBusiness(userData.businessID),
                getCompletedPayoutsByBusinessID(userData.businessID),
            ]);

            setBusiness(businessData || null);
            setEmployees(Array.isArray(employeeData) ? employeeData : []);
            setCompletedPayouts(Array.isArray(completed) ? completed : []);
            setIsLoading(false);
        };

        fetchData();
    }, [userData]);

    const getEmployeeFullName = (id) => {
        const emp = employees.find((employee) => employee.id === id);
        if (!emp) return id === "team" ? "Team Tip Pool" : "Unknown";
        return `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.displayName || "Unknown";
    };

    const sortedPayouts = useMemo(
        () =>
            [...completedPayouts].sort(
                (a, b) => (getTimestampDate(b.createdAt) || 0) - (getTimestampDate(a.createdAt) || 0)
            ),
        [completedPayouts]
    );

    const totalPaid = sortedPayouts.reduce((sum, payout) => sum + Number(payout.totalAmount || 0), 0);
    const totalTipsIncluded = sortedPayouts.reduce(
        (sum, payout) => sum + (Array.isArray(payout.tips) ? payout.tips.length : 0),
        0
    );
    const avgPayout = sortedPayouts.length ? totalPaid / sortedPayouts.length : 0;

    return (
        <BusinessLayoutV2>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500&display=swap');

                .biz-payouts-v2 * { box-sizing: border-box; margin: 0; padding: 0; }
                .biz-payouts-v2 {
                    --surface: #ffffff;
                    --surface-raised: #f0f0f5;
                    --border: #e8e8ee;
                    --text-primary: #1a1a2e;
                    --text-secondary: #6b6b82;
                    --text-muted: #9b9bb0;
                    --accent: #0d7c5f;
                    --accent-light: #e8f5f0;
                    --green: #0d7c5f;
                    --font: 'DM Sans', -apple-system, sans-serif;
                    --mono: 'JetBrains Mono', monospace;
                    font-family: var(--font);
                    color: var(--text-primary);
                    width: 100%;
                    padding: 32px 0 80px;
                }
                .biz-payouts-v2 .header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 26px;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .biz-payouts-v2 .title {
                    font-size: 28px;
                    font-weight: 700;
                    letter-spacing: -0.5px;
                }
                .biz-payouts-v2 .subtitle {
                    margin-top: 4px;
                    font-size: 14px;
                    color: var(--text-secondary);
                }
                .biz-payouts-v2 .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 14px;
                    margin-bottom: 28px;
                }
                @media (max-width: 700px) {
                    .biz-payouts-v2 .stats-grid { grid-template-columns: repeat(2, 1fr); }
                }
                .biz-payouts-v2 .stat-card {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 16px;
                    padding: 18px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .biz-payouts-v2 .stat-label {
                    font-size: 12px;
                    font-weight: 500;
                    color: var(--text-muted);
                    text-transform: uppercase;
                    letter-spacing: 0.7px;
                }
                .biz-payouts-v2 .stat-value {
                    font-size: 24px;
                    font-weight: 700;
                    letter-spacing: -0.8px;
                    font-family: var(--mono);
                    color: var(--green);
                }
                .biz-payouts-v2 .stat-suffix {
                    font-size: 12px;
                    color: var(--text-muted);
                }
                .biz-payouts-v2 .list-shell {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 18px;
                    overflow: hidden;
                }
                .biz-payouts-v2 .list-header {
                    background: var(--surface-raised);
                    border-bottom: 1px solid var(--border);
                    padding: 14px 18px;
                    font-size: 13px;
                    font-weight: 600;
                    color: var(--text-secondary);
                }
                .biz-payouts-v2 .payout-item {
                    padding: 18px;
                    border-bottom: 1px solid var(--border);
                }
                .biz-payouts-v2 .payout-item:last-child { border-bottom: none; }
                .biz-payouts-v2 .top-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 12px;
                    margin-bottom: 10px;
                }
                .biz-payouts-v2 .amount {
                    font-family: var(--mono);
                    font-size: 24px;
                    font-weight: 700;
                    color: var(--green);
                    letter-spacing: -0.8px;
                }
                .biz-payouts-v2 .emp-name {
                    font-size: 14px;
                    color: var(--text-secondary);
                    margin-top: 4px;
                }
                .biz-payouts-v2 .right {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 6px;
                }
                .biz-payouts-v2 .date {
                    font-size: 12px;
                    color: var(--text-muted);
                }
                .biz-payouts-v2 .status {
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 4px 10px;
                    border-radius: 6px;
                    background: var(--accent-light);
                    color: var(--accent);
                }
                .biz-payouts-v2 .tips-block {
                    margin-top: 8px;
                    background: var(--surface-raised);
                    border: 1px solid var(--border);
                    border-radius: 10px;
                    padding: 10px 12px;
                }
                .biz-payouts-v2 .tips-title {
                    font-size: 12px;
                    font-weight: 600;
                    color: var(--text-secondary);
                    margin-bottom: 8px;
                }
                .biz-payouts-v2 .tip-row {
                    display: flex;
                    justify-content: space-between;
                    gap: 10px;
                    font-size: 12px;
                    padding: 4px 0;
                }
                .biz-payouts-v2 .tip-amount {
                    font-family: var(--mono);
                    color: var(--text-primary);
                }
                .biz-payouts-v2 .tip-date {
                    color: var(--text-muted);
                    text-align: right;
                }
                .biz-payouts-v2 .empty {
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 14px;
                    color: var(--text-secondary);
                    font-weight: 500;
                    font-size: 14px;
                    padding: 16px;
                    text-align: center;
                }
                .biz-payouts-v2 .loading-wrap {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--surface);
                    border: 1px solid var(--border);
                    border-radius: 14px;
                    min-height: 140px;
                }
                .biz-payouts-v2 .spinner {
                    width: 28px;
                    height: 28px;
                    border: 3px solid #dbe8e2;
                    border-top-color: var(--accent);
                    border-radius: 9999px;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>

            <div className="biz-payouts-v2">
                <header className="header">
                    <div>
                        <h1 className="title">Business Payouts</h1>
                        <p className="subtitle">{business?.name || userData?.businessID || "Business"} · History</p>
                    </div>
                </header>

                <div className="stats-grid">
                    <div className="stat-card">
                        <span className="stat-label">Total Paid</span>
                        <span className="stat-value">€{totalPaid.toFixed(2)}</span>
                        <span className="stat-suffix">all payouts</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Payout Count</span>
                        <span className="stat-value">{sortedPayouts.length}</span>
                        <span className="stat-suffix">transactions</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Avg Payout</span>
                        <span className="stat-value">€{avgPayout.toFixed(2)}</span>
                        <span className="stat-suffix">per payout</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-label">Tips Included</span>
                        <span className="stat-value">{totalTipsIncluded}</span>
                        <span className="stat-suffix">across payouts</span>
                    </div>
                </div>

                {isLoading ? (
                    <div className="loading-wrap">
                        <div className="spinner" />
                    </div>
                ) : sortedPayouts.length === 0 ? (
                    <div className="empty">No payouts found.</div>
                ) : (
                    <div className="list-shell">
                        <div className="list-header">Payout History</div>
                        {sortedPayouts.map((payout) => {
                            const tips = Array.isArray(payout.tips) ? payout.tips : [];
                            return (
                                <article key={payout.id} className="payout-item">
                                    <div className="top-row">
                                        <div>
                                            <div className="amount">€{Number(payout.totalAmount || 0).toFixed(2)}</div>
                                            <div className="emp-name">{getEmployeeFullName(payout.employeeID)}</div>
                                        </div>
                                        <div className="right">
                                            <span className="status">{String(payout.status || "pending")}</span>
                                            <span className="date">{formatDateTime(payout.createdAt)}</span>
                                        </div>
                                    </div>

                                    <div className="tips-block">
                                        <p className="tips-title">Included Tips</p>
                                        {tips.length ? (
                                            tips.map((tip) => (
                                                <div key={tip.id} className="tip-row">
                                                    <span className="tip-amount">€{Number(tip.amount || 0).toFixed(2)}</span>
                                                    <span className="tip-date">{formatDateTime(tip.createdAt)}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="tip-row">
                                                <span className="tip-date">No tip breakdown available</span>
                                            </div>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </BusinessLayoutV2>
    );
}
